import { z } from 'zod';
import type {
  BatchScanProgress,
  BatchWaferTransferJobProgress,
} from '@cnapse/shared/schemas.js';
import { UNIFIED_JOB_TYPE } from '@cnapse/shared/data.types.js';
import { appNavigate } from './appNavigate';
import { isTerminalBatchWaferTransferJobPhase } from './batchWaferTransferProgressAdapter';
import { isTerminalContainerSlotScanJobPhase } from './containerSlotScanProgressAdapter';
import type { CONTAINER_SLOT_SCAN_JOB_PHASE } from '@cnapse/shared/data.types.js';
import type { TransferConfig, TransferRow } from '../components/operations/wizard/TransferConfigStep';
import type { ResourceSelectionValue } from '../components/operations/wizard/ResourceSelectionStep';
import type { SlotScanConfig } from '../components/operations/wizard/SlotScanConfigStep';
import type { JobTypeChoice } from '../components/operations/wizard/JobTypeStep';

const JOB_WIZARD_VIEW_CONTEXT_KEY = 'job-wizard-view-context';

const jobWizardViewContextSchema = z.object({
  jobId: z.string().min(1),
  jobType: z.enum(['batch_wafer_transfer', 'container_slot_scan']),
  efemClientId: z.number().int().positive().optional(),
  efemClientName: z.string().nullable().optional(),
  gatewaySerialNumber: z.string().min(1).nullable().optional(),
  loadPortId: z.number().int().positive().optional(),
});

export type JobWizardViewContext = z.infer<typeof jobWizardViewContextSchema>;

export function saveJobWizardViewContext(payload: JobWizardViewContext): void {
  try {
    sessionStorage.setItem(JOB_WIZARD_VIEW_CONTEXT_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

export function consumeJobWizardViewContext(jobId: string): JobWizardViewContext | null {
  try {
    const raw = sessionStorage.getItem(JOB_WIZARD_VIEW_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = jobWizardViewContextSchema.safeParse(JSON.parse(raw));
    sessionStorage.removeItem(JOB_WIZARD_VIEW_CONTEXT_KEY);
    if (!parsed.success || parsed.data.jobId !== jobId) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function buildJobWizardViewUrl(jobId: string, jobType: JobTypeChoice): string {
  const params = new URLSearchParams({
    viewJobId: jobId,
    jobType,
  });
  return `/operations/jobs/new?${params.toString()}`;
}

/** Read-only review UI (wizard step 5) for a completed or historical container slot scan. */
export function buildContainerSlotScanReviewUrl(jobId: string): string {
  return `/operations/jobs/${encodeURIComponent(jobId)}/review`;
}

/** Open the wizard monitor step for an in-progress or completed EFEM job. */
export function navigateToJobWizardView(context: JobWizardViewContext): void {
  saveJobWizardViewContext({
    ...context,
    gatewaySerialNumber: context.gatewaySerialNumber ?? null,
  });
  appNavigate(buildJobWizardViewUrl(context.jobId, context.jobType));
}

export function parseJobWizardViewParams(
  searchParams: URLSearchParams,
): { viewJobId: string | null; jobType: string | null } {
  return {
    viewJobId: searchParams.get('viewJobId') ?? searchParams.get('monitorJobId'),
    jobType: searchParams.get('jobType'),
  };
}

export function isJobWizardViewMode(
  viewJobId: string | null,
  jobTypeParam: string | null,
): jobTypeParam is JobTypeChoice {
  if (!viewJobId || !jobTypeParam) {
    return false;
  }
  return (
    jobTypeParam === UNIFIED_JOB_TYPE.BATCH_WAFER_TRANSFER ||
    jobTypeParam === UNIFIED_JOB_TYPE.CONTAINER_SLOT_SCAN
  );
}

export function isTransferViewComplete(phase: string): boolean {
  return isTerminalBatchWaferTransferJobPhase(phase);
}

export function isScanViewComplete(phase: string): boolean {
  return isTerminalContainerSlotScanJobPhase(phase as CONTAINER_SLOT_SCAN_JOB_PHASE);
}

export function loadPortIdsFromTransferProgress(
  progress: BatchWaferTransferJobProgress,
): number[] {
  const seen = new Set<number>();
  const ids: number[] = [];
  const add = (id: number) => {
    if (!seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  };
  for (const item of progress.items) {
    add(item.fromLoadPort);
    add(item.toLoadPort);
  }
  return ids.slice(0, 2);
}

let transferRowIdCounter = 1;

export function transferConfigFromProgress(
  progress: BatchWaferTransferJobProgress,
): TransferConfig {
  const rows: TransferRow[] = progress.items.map((item) => ({
    id: transferRowIdCounter++,
    fromLoadPortId: item.fromLoadPort,
    fromSlot: item.fromSlot,
    toLoadPortId: item.toLoadPort,
    toSlot: item.toSlot,
    sourceContainerId: item.fromContainerId,
    destContainerId: item.toContainerId,
  }));
  return { rows };
}

export function resourceFromTransferView(
  context: JobWizardViewContext | null,
  progress: BatchWaferTransferJobProgress,
): ResourceSelectionValue {
  const loadPortIds = loadPortIdsFromTransferProgress(progress);
  return {
    efemClientId: context?.efemClientId ?? null,
    efemClientName: context?.efemClientName ?? null,
    gatewaySerialNumber: context?.gatewaySerialNumber ?? null,
    loadPortIds,
    boundContainerSize: null,
    allSelectedPortsBound: loadPortIds.length > 0,
  };
}

export function scanConfigFromProgress(progress: BatchScanProgress): SlotScanConfig {
  const selectedSlots = [...progress.results.map((r) => r.slotNumber)].sort((a, b) => a - b);
  return {
    selectedSlots,
    totalSlots: progress.totalSlots,
    slotMapSlots: selectedSlots.map((slotNumber) => ({
      slotNumber,
      isOccupied: true,
    })),
    waferImagingTemplateId: 0,
    lightingSource: 'template_starting',
  };
}

export function resourceFromScanView(
  context: JobWizardViewContext | null,
  loadPortId: number | undefined,
): ResourceSelectionValue {
  return {
    efemClientId: context?.efemClientId ?? null,
    efemClientName: context?.efemClientName ?? null,
    gatewaySerialNumber: context?.gatewaySerialNumber ?? null,
    loadPortIds: loadPortId != null ? [loadPortId] : [],
    boundContainerSize: null,
    allSelectedPortsBound: loadPortId != null,
  };
}
